import {
    collection,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    getDocs,
    getDoc,
    query,
    where,
    orderBy,
    serverTimestamp,
    Timestamp,
} from 'firebase/firestore';
import { db } from './config';
import { uploadImageToCloudinary } from '../services/cloudinaryService';

// Experience data interface
export interface Experience {
    id?: string;
    company: string;
    position: string;
    location?: string;
    startDate: string; // Format: "YYYY-MM" or "YYYY-MM-DD"
    endDate?: string;  // Format: "YYYY-MM" or "YYYY-MM-DD", null means "Present"
    descriptions: string[];
    companyLogo?: string;
    website?: string;
    order?: number;
    createdAt?: Timestamp;
    updatedAt?: Timestamp;
}

// Format display date from YYYY-MM to display format like "Jan 2022"
export const formatDisplayDate = (dateStr: string | undefined, isPresent?: boolean): string => {
    if (!dateStr && isPresent) return 'Present';
    if (!dateStr) return '';

    const [year, month] = dateStr.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

// Format display duration from two dates
export const formatDateRange = (startDate: string, endDate?: string): string => {
    const start = formatDisplayDate(startDate);
    const end = endDate ? formatDisplayDate(endDate) : 'Present';
    return `${start} - ${end}`;
};

// Collection reference
const experiencesCollection = collection(db, 'experiences');

/**
 * Get all experiences
 */
export const getAllExperiences = async (): Promise<Experience[]> => {
    try {
        const experiencesQuery = query(experiencesCollection, orderBy('order', 'asc'));
        const querySnapshot = await getDocs(experiencesQuery);

        return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        } as Experience));
    } catch (error) {
        console.error('Error getting experiences:', error);
        throw error;
    }
};

/**
 * Get an experience by ID
 */
export const getExperienceById = async (id: string): Promise<Experience | null> => {
    try {
        const docRef = doc(db, 'experiences', id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            return {
                id: docSnap.id,
                ...docSnap.data()
            } as Experience;
        } else {
            return null;
        }
    } catch (error) {
        console.error('Error getting experience:', error);
        throw error;
    }
};

/**
 * Create a new experience
 */
export const createExperience = async (experienceData: Omit<Experience, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
    try {
        // Get current max order and add 1
        const experiences = await getAllExperiences();
        const maxOrder = experiences.length > 0
            ? Math.max(...experiences.map(exp => exp.order || 0))
            : -1;

        const docRef = await addDoc(experiencesCollection, {
            ...experienceData,
            order: maxOrder + 1,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        });

        return docRef.id;
    } catch (error) {
        console.error('Error creating experience:', error);
        throw error;
    }
};

/**
 * Update an experience
 */
export const updateExperience = async (id: string, experienceData: Partial<Omit<Experience, 'id' | 'createdAt' | 'updatedAt'>>): Promise<void> => {
    try {
        const docRef = doc(db, 'experiences', id);
        await updateDoc(docRef, {
            ...experienceData,
            updatedAt: serverTimestamp()
        });
    } catch (error) {
        console.error('Error updating experience:', error);
        throw error;
    }
};

/**
 * Delete an experience
 */
export const deleteExperience = async (id: string): Promise<void> => {
    try {
        const docRef = doc(db, 'experiences', id);
        await deleteDoc(docRef);

        // Re-order remaining experiences
        await reorderExperiences();
    } catch (error) {
        console.error('Error deleting experience:', error);
        throw error;
    }
};

/**
 * Reorder experiences after deletion or manual reordering
 */
export const reorderExperiences = async (): Promise<void> => {
    try {
        const experiences = await getAllExperiences();

        // Sort first to ensure proper order
        experiences.sort((a, b) => (a.order || 0) - (b.order || 0));

        // Update each document with its new order
        const updatePromises = experiences.map((experience, index) => {
            if (!experience.id) return Promise.resolve();
            const docRef = doc(db, 'experiences', experience.id);
            return updateDoc(docRef, { order: index });
        });

        await Promise.all(updatePromises);
    } catch (error) {
        console.error('Error reordering experiences:', error);
        throw error;
    }
};

/**
 * Move experience up in order
 */
export const moveExperienceUp = async (id: string): Promise<void> => {
    try {
        const experiences = await getAllExperiences();
        const index = experiences.findIndex(exp => exp.id === id);

        if (index <= 0) return; // Already at the top

        // Swap with previous experience
        const currentExp = experiences[index];
        const prevExp = experiences[index - 1];

        if (currentExp.id && prevExp.id) {
            await updateExperience(currentExp.id, { order: index - 1 });
            await updateExperience(prevExp.id, { order: index });
        }
    } catch (error) {
        console.error('Error moving experience up:', error);
        throw error;
    }
};

/**
 * Move experience down in order
 */
export const moveExperienceDown = async (id: string): Promise<void> => {
    try {
        const experiences = await getAllExperiences();
        const index = experiences.findIndex(exp => exp.id === id);

        if (index === -1 || index >= experiences.length - 1) return; // Already at the bottom

        // Swap with next experience
        const currentExp = experiences[index];
        const nextExp = experiences[index + 1];

        if (currentExp.id && nextExp.id) {
            await updateExperience(currentExp.id, { order: index + 1 });
            await updateExperience(nextExp.id, { order: index });
        }
    } catch (error) {
        console.error('Error moving experience down:', error);
        throw error;
    }
};

/**
 * Upload company logo to Cloudinary
 */
export const uploadCompanyLogo = async (
    file: File,
    progressCallback?: (progress: number) => void
): Promise<string> => {
    try {
        // Use Cloudinary service to upload the file
        const imageUrl = await uploadImageToCloudinary(file, progressCallback);
        return imageUrl;
    } catch (error) {
        console.error('Error uploading company logo to Cloudinary:', error);
        throw error;
    }
}; 